import { useEffect, useState, useRef } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { api, clearGuestAuth, clearAdminAuth, persistGuestAuth, persistAdminSession } from "@/lib/api";
import { isTransientHttpError, storedDeskSession, optimisticDeskAuthState, withTimeout } from "@/lib/authBoot";
import useQuiescentAwarePolling from "@/hooks/useQuiescentAwarePolling";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import OiPulseLogo from "@/components/OiPulseLogo";
import AuthShell from "@/components/AuthShell";
import MaintenanceScreen from "@/components/MaintenanceScreen";
import DataLoadingState from "@/components/DataLoadingState";

function MembershipAccessNotice({ plan, availablePlans = ["premium", "pro"] }) {
  const planName = plan === "normal" ? "Basic" : plan === "pro" ? "Pro" : "Premium";
  const planLabels = { premium: "StrikLenz Premium", pro: "StrikLenz Pro" };
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10 text-slate-900">
      <section className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-xl sm:p-9">
        <OiPulseLogo className="mx-auto mb-4 h-12 w-12" pulse={false} />
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">StrikLenz membership</p>
        <h1 className="mt-2 text-2xl font-bold">The {planName} plan is currently unavailable</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          Your account is still active, but this plan is paused. Choose an available subscription to continue to the market desk, or return later if you want to use Basic access.
        </p>
        {availablePlans.length > 0 ? (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {availablePlans.map((planId) => (
              <Link
                key={planId}
                to={`/account?plan=${planId}`}
                className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800"
              >
                View {planLabels[planId] || "subscription"}
              </Link>
            ))}
          </div>
        ) : (
          <p className="mt-5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
            Subscriptions are not available right now. Please check back later.
          </p>
        )}
        <Link to="/account" className="mt-5 inline-block text-sm font-semibold text-slate-600 underline underline-offset-4">
          Open account settings
        </Link>
      </section>
    </main>
  );
}

/**
 * AuthGate — admin, durable Google member, and guest entry:
 *   1. Admin login (default when public access is closed)
 *   2. Guest name prompt (when public access is open but caller has no guest token)
 *   3. Pass-through (admin, Google member, or guest already authenticated)
 *
 * Admin Remember-me restores via /auth/remember-login. Admin is NOT kicked at
 * market close unless Settings → expire_admin_on_market_close is explicitly ON.
 */
export default function AuthGate({ children }) {
  const location = useLocation();
  const [state, setState] = useState(() => optimisticDeskAuthState() || {
    loading: true, requires_login: true, is_admin: false, is_guest: false, needs_guest_name: false,
  });
  const [guestName, setGuestName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(() => {
    try {
      const id = sessionStorage.getItem("oi_access_request_id");
      const name = sessionStorage.getItem("oi_access_request_name");
      return id ? { id, name: name || "" } : null;
    } catch (_) {
      return null;
    }
  });
  const [waitStatus, setWaitStatus] = useState(null); // pending | rejected | null
  const lastActivityRef = useRef(Date.now());
  const pendingPollRef = useRef(null);
  const admittingRef = useRef(false);
  const ipMismatchToastedRef = useRef(false);

  const refresh = async () => {
    try {
      // Strip stale localStorage admin tokens — they blocked Remember-me forever.
      try {
        const staleLocal = localStorage.getItem("oi_admin_token");
        const sessTok = sessionStorage.getItem("oi_admin_token");
        if (staleLocal && !sessTok) localStorage.removeItem("oi_admin_token");
      } catch (_) { /* noop */ }

      const rememberTok = (() => {
        try { return localStorage.getItem("oi_admin_remember_token"); } catch (_) { return null; }
      })();
      const hasSessionAdmin = (() => {
        try { return !!sessionStorage.getItem("oi_admin_token"); } catch (_) { return false; }
      })();

      // /auth/state first so a hung Remember-me cannot pin the dark Loading screen.
      let data;
      let lastErr;
      try {
        const res = await withTimeout(api.get("/auth/state", { timeout: 2500 }), 2800, "auth state timeout");
        data = res.data;
      } catch (err) {
        lastErr = err;
      }
      if (!data) throw lastErr || new Error("auth state unavailable");

      if (data.requires_login && !data.is_admin && !data.is_member) {
        clearGuestAuth();
      }
      if (!data.is_guest && !data.is_admin && !data.is_member) {
        if (!data.public_access_open) clearGuestAuth();
      }
      if (data.suggested_guest_name) {
        setGuestName((prev) => prev || data.suggested_guest_name);
      }
      setState({ loading: false, ...data });
      try {
        window.__oi_last_auth_state = data;
        window.dispatchEvent(new CustomEvent("oi-admin-auth-state", { detail: data }));
      } catch (_) { /* noop */ }

      if (rememberTok && !hasSessionAdmin && !data?.is_admin) {
        try {
          const { data: rem } = await withTimeout(
            api.post("/auth/remember-login", { remember_token: rememberTok }, { timeout: 2000 }),
            2200,
            "remember-login timeout",
          );
          if (rem?.token) {
            persistAdminSession(rem.token);
            const { data: again } = await withTimeout(api.get("/auth/state", { timeout: 2500 }), 2800, "auth state timeout");
            setState({ loading: false, ...again });
            try {
              window.__oi_last_auth_state = again;
              window.dispatchEvent(new CustomEvent("oi-admin-auth-state", { detail: again }));
            } catch (_) { /* noop */ }
          }
        } catch (err) {
          if (!isTransientHttpError(err)) {
            const detail = String(err?.response?.data?.detail || "");
            if (/expired|invalid|missing/i.test(detail)) {
              try { localStorage.removeItem("oi_admin_remember_token"); } catch (_) {}
            }
          }
        }
      }
    } catch (err) {
      if (isTransientHttpError(err)) {
        let last = null;
        try { last = window.__oi_last_auth_state; } catch (_) { /* noop */ }
        if (last?.is_admin) {
          setState({ loading: false, ...last });
          return;
        }
        const stored = storedDeskSession();
        if (stored) {
          setState({
            loading: false,
            requires_login: false,
            public_access_open: true,
            is_admin: !!stored.is_admin,
            is_guest: !!stored.is_guest,
            needs_guest_name: false,
          });
          return;
        }
        setState({
          loading: false,
          auth_unavailable: true,
          requires_login: false,
          is_admin: false,
          is_guest: false,
          needs_guest_name: false,
        });
        return;
      }
      setState({ loading: false, requires_login: true, is_admin: false, is_guest: false, needs_guest_name: false });
    }
  };

  useEffect(() => {
    import("@/pages/Dashboard");
  }, []);
  useEffect(() => {
    const failOpen = setTimeout(() => {
      setState((s) => {
        if (!s.loading) return s;
        const stored = storedDeskSession();
        if (stored?.is_admin) {
          return {
            loading: false,
            requires_login: false,
            public_access_open: true,
            is_admin: !!stored.is_admin,
            is_guest: false,
            needs_guest_name: false,
          };
        }
        return {
          loading: false,
          auth_unavailable: true,
          requires_login: false,
          is_admin: false,
          is_guest: false,
          needs_guest_name: false,
        };
      });
    }, 1500);
    return () => clearTimeout(failOpen);
  }, []);
  useEffect(() => {
    const bump = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener("mousemove", bump);
    window.addEventListener("keydown", bump);
    window.addEventListener("click", bump);
    return () => {
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("click", bump);
    };
  }, []);

  // Auth must keep working after EOD.
  useQuiescentAwarePolling(refresh, 60_000, [], {
    immediate: true,
    allowDuringQuiescent: true,
    dedupeKey: "auth-gate",
  });

  // Absolute session TTL logout for admin (matches backend created_at + ttl).
  // Does NOT call /auth/logout (that would wipe Remember-me for this IP).
  useEffect(() => {
    if (!state.is_admin || state.local_dev_admin_bypass) return;
    const ttl = (state.session_ttl_seconds || 8 * 3600) * 1000;
    const check = setInterval(() => {
      if (Date.now() - lastActivityRef.current > ttl) {
        toast.info("Signed out — session timed out.");
        clearAdminAuth({ clearRemember: false });
        window.location.reload();
      }
    }, 60_000);
    return () => clearInterval(check);
  }, [state.is_admin, state.local_dev_admin_bypass, state.session_ttl_seconds]);

  // Market-close admin logout — ONLY when Settings explicitly enables it.
  useEffect(() => {
    if (!state.is_admin || state.local_dev_admin_bypass) return;
    if (!state.expire_admin_on_market_close) return;
    if (!state.admin_session_expires_at) return;
    const expMs = Date.parse(state.admin_session_expires_at);
    if (Number.isNaN(expMs)) return;
    const now = Date.now();
    if (expMs <= now) {
      toast.info("Signed out — market closed.");
      clearAdminAuth({ clearRemember: false });
      window.location.reload();
      return;
    }
    const timer = setTimeout(() => {
      toast.info("Signed out — market closed.");
      clearAdminAuth({ clearRemember: false });
      window.location.reload();
    }, Math.min(expMs - now, 2147483000));
    return () => clearTimeout(timer);
  }, [state.is_admin, state.local_dev_admin_bypass, state.admin_session_expires_at, state.expire_admin_on_market_close]);

  const admitGuest = async (token, name, expiresIn, expiresAt) => {
    clearAdminAuth({ clearRemember: false });
    persistGuestAuth({ token, name: name || "", expiresInSeconds: expiresIn, expiresAt });
    try {
      sessionStorage.removeItem("oi_access_request_id");
      sessionStorage.removeItem("oi_access_request_name");
    } catch (_) {}
    setPendingRequest(null);
    setWaitStatus(null);
    toast.success(`Welcome, ${name || "guest"}`);
    await refresh();
  };

  // If admin turns Require approval OFF while a guest is waiting, admit them.
  useEffect(() => {
    if (state.loading || state.is_guest || state.is_admin) return undefined;
    if (state.guest_require_approval !== false) return undefined;
    if (!pendingRequest) return undefined;
    const name = (pendingRequest.name || guestName || "").trim();
    if (name.length < 2 || !name.includes(" ")) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/guest", { name });
        if (cancelled) return;
        if (data?.token) {
          await admitGuest(data.token, data.name || name, data.expires_in_seconds, data.expires_at);
        }
      } catch (_) { /* stay on wait screen */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.guest_require_approval, pendingRequest?.id, state.is_guest, state.is_admin]);

  // Poll pending access request until admin decides.
  useEffect(() => {
    if (!pendingRequest?.id || state.is_guest || state.is_admin) return undefined;
    let cancelled = false;
    ipMismatchToastedRef.current = false;
    admittingRef.current = false;
    const stop = () => {
      if (pendingPollRef.current) {
        clearInterval(pendingPollRef.current);
        pendingPollRef.current = null;
      }
    };
    const poll = async () => {
      if (cancelled || admittingRef.current) return;
      try {
        const { data } = await api.get(`/auth/access-request/${pendingRequest.id}`);
        if (cancelled || admittingRef.current) return;
        if ((data.status === "approved" || data.status === "consumed") && data.token) {
          admittingRef.current = true;
          stop();
          await admitGuest(data.token, data.name || pendingRequest.name, data.expires_in_seconds, data.expires_at);
          return;
        }
        if (data.status === "consumed" && !data.token) {
          // Token already handed off (other tab) — try auto-guest revive, else re-prompt.
          stop();
          try {
            sessionStorage.removeItem("oi_access_request_id");
            sessionStorage.removeItem("oi_access_request_name");
          } catch (_) {}
          setPendingRequest(null);
          setWaitStatus(null);
          await refresh();
          toast.message("Already approved", {
            description: "If you are not in yet, enter the same full name once more.",
          });
          return;
        }
        if (data.status === "rejected") {
          stop();
          setWaitStatus("rejected");
          try {
            sessionStorage.removeItem("oi_access_request_id");
            sessionStorage.removeItem("oi_access_request_name");
          } catch (_) {}
          toast.error("Access request was rejected by the admin.");
          setPendingRequest(null);
          return;
        }
        setWaitStatus("pending");
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          setWaitStatus("pending");
          if (!ipMismatchToastedRef.current) {
            ipMismatchToastedRef.current = true;
            toast.error("This approval link is tied to another network. Stay on the same connection you used to request access.");
          }
          return;
        }
        if (status === 404) {
          stop();
          try {
            sessionStorage.removeItem("oi_access_request_id");
            sessionStorage.removeItem("oi_access_request_name");
          } catch (_) {}
          setPendingRequest(null);
          setWaitStatus(null);
          toast.error("Access request expired. Please request again.");
        }
        // other transient errors — keep waiting
      }
    };
    poll();
    pendingPollRef.current = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRequest?.id, state.is_guest, state.is_admin]);

  const doGuest = async (e) => {
    e?.preventDefault();
    const name = guestName.trim();
    if (name.length < 2 || !name.includes(" ")) {
      toast.error("Please enter your FULL name (first name + last name).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/guest", { name });
      // Returning guest — minted immediately (already approved for this IP + name).
      if (data?.token) {
        await admitGuest(data.token, data.name || name, data.expires_in_seconds, data.expires_at);
        return;
      }
      // New flow: pending until admin approves.
      if (data?.status === "pending" && data.request_id) {
        try {
          sessionStorage.setItem("oi_access_request_id", data.request_id);
          sessionStorage.setItem("oi_access_request_name", data.name || name);
        } catch (_) {}
        setPendingRequest({ id: data.request_id, name: data.name || name });
        setWaitStatus("pending");
        toast.message("Request sent", { description: "Waiting for admin approval…" });
        return;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not request access";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return <DataLoadingState />;
  }

  if (state.auth_unavailable) {
    return <MaintenanceScreen retrying={state.loading} onRetry={() => {
      setState((s) => ({ ...s, loading: true, auth_unavailable: false }));
      refresh();
    }} />;
  }

  if (state.maintenance_mode && !state.is_admin) {
    return <MaintenanceScreen retrying={state.loading} onRetry={() => {
      setState((s) => ({ ...s, loading: true }));
      refresh();
    }} />;
  }

  if (state.ip_blocked && !state.is_admin) {
    return (
      <AuthShell mode="guest">
        <div
          data-testid="guest-ip-blocked"
          className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-900 shadow-2xl"
        >
          <OiPulseLogo className="mx-auto mb-4 h-12 w-12" />
          <h1 className="text-xl font-semibold">Unable to process request</h1>
          <p className="mt-2 text-sm text-slate-600">
            Unable to process request at this moment.
          </p>
        </div>
      </AuthShell>
    );
  }

  // Member sessions are account-authenticated and independent of the guest door.
  if (state.is_admin) {
    return (
      <>
        {state.local_dev_admin_bypass && (
          <div
            role="status"
            aria-live="polite"
            className="fixed bottom-3 right-3 z-[100] rounded-md border border-amber-400/50 bg-amber-950/95 px-3 py-2 text-xs font-semibold text-amber-100 shadow-lg"
          >
            Local development — admin login bypassed
          </div>
        )}
        {children}
      </>
    );
  }
  if (state.is_member) {
    if (state.membership?.enabled === false && location.pathname !== "/account") {
      return (
        <MembershipAccessNotice
          plan={state.membership.plan}
          availablePlans={state.membership.available_paid_plans}
        />
      );
    }
    return children;
  }
  // Guest sessions remain controlled by the separate public-access switch.
  if (state.is_guest && state.public_access_open) return children;
  // Stale guest token after public access closed → force login/guest prompt
  if (state.is_guest && !state.public_access_open) {
    clearGuestAuth();
  }

  // Not authenticated → route everyone to the branded /login page.
  if (state.needs_guest_name || state.public_access_open) {
    return <Navigate to="/login" replace />;
  }
  if (typeof window === "undefined") {
    return (
      <AuthShell mode="guest">
        <div className="oi-auth-login-card w-full max-w-md rounded-2xl border border-emerald-300/35 p-7 text-white shadow-2xl shadow-black/50 sm:p-8">
          <div className="mb-6 flex items-center gap-3">
            <OiPulseLogo className="h-11 w-11" />
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Welcome to StrikLenz</h2>
              <p className="text-sm text-slate-300">
                {state.guest_require_approval === false
                  ? "Enter your full name to open the desk"
                  : "Request read-only entry to the desk"}
              </p>
            </div>
          </div>

          <form onSubmit={doGuest} className="space-y-4" data-testid="guest-form">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-300">Full name</Label>
              {state.suggested_guest_name ? (
                <p className="mb-1.5 text-xs text-emerald-300" data-testid="guest-welcome-back">
                  Welcome back — we remembered your name from this device.
                </p>
              ) : null}
              <Input
                data-testid="guest-name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="e.g. Rahul Sharma"
                autoFocus
                disabled={!!pendingRequest}
                className="oi-auth-login-input mt-1 h-11"
              />
            </div>

            {waitStatus === "rejected" ? (
              <div
                data-testid="guest-rejected"
                className="space-y-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-950"
              >
                <div className="font-semibold">Request declined</div>
                <div className="text-xs opacity-80">
                  The admin rejected this access request. You can submit a new request with your full name.
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-full rounded-xl border-rose-200 bg-white text-rose-900 hover:bg-rose-50"
                  onClick={() => {
                    setWaitStatus(null);
                    setPendingRequest(null);
                  }}
                >
                  Request again
                </Button>
              </div>
            ) : pendingRequest || waitStatus === "pending" ? (
              <div
                data-testid="guest-waiting-approval"
                className="space-y-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900"
              >
                <div className="flex items-center gap-2 font-semibold">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                  Waiting for admin approval…
                </div>
                <div className="text-xs opacity-80">
                  Requested as <b>{pendingRequest?.name || guestName}</b>. Keep this window open — you&apos;ll enter automatically when approved. No refresh needed.
                </div>
                <button
                  type="button"
                  className="pt-1 text-xs underline opacity-70 hover:opacity-100"
                  onClick={() => {
                    try {
                      sessionStorage.removeItem("oi_access_request_id");
                      sessionStorage.removeItem("oi_access_request_name");
                    } catch (_) {}
                    setPendingRequest(null);
                    setWaitStatus(null);
                  }}
                >
                  Cancel request
                </button>
              </div>
            ) : (
              <Button
                data-testid="guest-submit"
                type="submit"
                className="h-11 w-full rounded-xl bg-emerald-600 text-white hover:bg-emerald-500"
                disabled={busy}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  {busy ? "Entering…" : state.guest_require_approval === false ? "Enter desk" : "Request access"}
                </span>
              </Button>
            )}

            <p className="text-center text-xs text-slate-300">
              {state.guest_require_approval === false
                ? "Your name is recorded. You get the guest pages immediately. Blocked networks cannot enter."
                : "New guests need admin approval. Returning guests on this network enter immediately when the same name was approved before."}
            </p>
          </form>

          <div className="mt-5 border-t border-white/10 pt-4 text-center text-xs text-slate-300">
            Admin?{" "}
            <Link to="/admin" className="font-medium text-emerald-300 hover:underline">
              Sign in here
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  // Default → branded login page
  return <Navigate to="/login" replace />;
}